import React, { useEffect, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress,
    Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination,
    TableRow, TextField, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { StatutoryRegisterService } from '../../services/statutoryRegister.service';
import { IBonusRegister } from '../../interfaces/statutoryRegister.interface';
import { useToast } from '../../context/ToastContext';
import { friendlyNote, money, monthYearLabel, triggerDownload } from './shared';
import { humanizeEnum } from '../../utils/format';

interface Props {
    institutionId: string;
}

const currentYear = () => new Date().getFullYear();
const EMPLOYEE_TOTALS_PAGE_SIZE = 10;

const BonusRegisterTab: React.FC<Props> = ({ institutionId }) => {
    const toast = useToast();
    const [year, setYear] = useState(currentYear());
    const [register, setRegister] = useState<IBonusRegister | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalsPage, setTotalsPage] = useState(0);

    const load = async () => {
        // M-3: a cleared/mid-edit year field parses to NaN — never fire the request with it.
        if (Number.isNaN(year)) return;
        setLoading(true);
        setPage(0);
        setTotalsPage(0);
        try {
            const data = await StatutoryRegisterService.getBonusRegister(institutionId, year);
            setRegister(data);
            setLoadError(null);
        } catch (e) {
            // M-5: clear stale data on a failed reload so the table never shows a prior year's
            // figures under the newly-selected year.
            setRegister(null);
            setLoadError('Could not load the bonus register.');
            toast.showApiError(e, 'Failed to load bonus register');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleDownload = async () => {
        if (!register) return;
        setDownloading(true);
        try {
            const blob = await StatutoryRegisterService.downloadBonusRegisterXlsx(institutionId, register.year);
            triggerDownload(blob, `bonus-register-${register.year}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!register) return;
        setDownloadingPdf(true);
        try {
            const blob = await StatutoryRegisterService.downloadBonusRegisterPdf(institutionId, register.year);
            triggerDownload(blob, `bonus-register-${register.year}.pdf`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloadingPdf(false);
        }
    };

    const pagedRows = register
        ? register.rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : [];
    const pagedTotals = register
        ? register.employeeTotals.slice(
            totalsPage * EMPLOYEE_TOTALS_PAGE_SIZE, totalsPage * EMPLOYEE_TOTALS_PAGE_SIZE + EMPLOYEE_TOTALS_PAGE_SIZE,
        )
        : [];

    return (
        <Box data-help-page="statutory.bonus.page">
            <Box data-help-section="statutory.bonus.filter" display="flex" gap={2} alignItems="flex-end" mb={2} flexWrap="wrap">
                <TextField
                    size="small"
                    label="Year"
                    type="number"
                    data-help="statutory.bonus.year"
                    value={Number.isNaN(year) ? '' : year}
                    onChange={e => {
                        const v = parseInt(e.target.value, 10);
                        if (!Number.isNaN(v)) setYear(v);
                    }}
                    sx={{ width: 120 }}
                />
                <Button data-help="statutory.bonus.load" variant="outlined" size="small" onClick={load} disabled={loading}>Load</Button>
                <Button data-help="statutory.bonus.downloadXlsx"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownload} disabled={!register || downloading}
                >
                    Download XLSX
                </Button>
                <Button data-help="statutory.bonus.downloadPdf"
                    variant="outlined" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadPdf} disabled={!register || downloadingPdf}
                >
                    Download PDF
                </Button>
            </Box>

            {loading && <CircularProgress size={24} />}

            {!loading && loadError && (
                <Alert severity="error" sx={{ mb: 2 }} data-testid="bonus-load-error">{loadError}</Alert>
            )}

            {!loading && register && (
                <>
                    {register.unreconciled.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }} data-testid="bonus-unreconciled-banner" data-help="statutory.bonus.unreconciled">
                            <Typography variant="body2" fontWeight={600}>
                                Unreconciled rows ({register.unreconciled.length})
                            </Typography>
                            {register.unreconciled.map((u, i) => (
                                <Typography key={`${u.userId}-${u.month}-${i}`} variant="body2">
                                    {u.employeeName ?? u.userId} — {monthYearLabel(register.year, u.month)}
                                </Typography>
                            ))}
                        </Alert>
                    )}

                    {register.caveats.map((c, i) => (
                        <Alert key={i} severity="info" sx={{ mb: 1 }} data-help="statutory.bonus.caveat">{friendlyNote(c)}</Alert>
                    ))}

                    <TableContainer component={Paper} data-testid="bonus-register-table" data-help-section="statutory.bonus.table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="statutory.bonus.col.employee">Employee</TableCell>
                                    <TableCell data-help="statutory.bonus.col.code">Code</TableCell>
                                    <TableCell data-help="statutory.bonus.col.month">Month</TableCell>
                                    <TableCell data-help="statutory.bonus.col.status">Status</TableCell>
                                    <TableCell align="right" data-help="statutory.bonus.col.festival">Festival Bonus</TableCell>
                                    <TableCell align="right" data-help="statutory.bonus.col.attendance">Attendance Bonus</TableCell>
                                    <TableCell data-help="statutory.bonus.col.occasions">Occasion Lines</TableCell>
                                    <TableCell data-help="statutory.bonus.col.reconciled">Reconciled</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <Typography color="text.secondary">No rows for this year.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {pagedRows.map((row, idx) => (
                                    <TableRow key={`${row.userId}-${row.month}-${idx}`} hover>
                                        <TableCell>{row.employeeName ?? '—'}</TableCell>
                                        <TableCell>{row.employeeCode ?? '—'}</TableCell>
                                        <TableCell>{monthYearLabel(register.year, row.month)}</TableCell>
                                        <TableCell>{row.payrollStatus ? humanizeEnum(row.payrollStatus) : '—'}</TableCell>
                                        <TableCell align="right">{money(row.festivalBonusAmount)}</TableCell>
                                        <TableCell align="right">{money(row.attendanceBonusAmount)}</TableCell>
                                        <TableCell>
                                            {row.occasionLines.map((line, li) => (
                                                <Typography key={li} variant="body2" component="div">
                                                    {line.occasion}: {money(line.amount)}
                                                    {line.percentOfBasic ? ` (${line.percentOfBasic})` : ''}
                                                </Typography>
                                            ))}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={row.reconciled ? 'Yes' : 'No'}
                                                color={row.reconciled ? 'success' : 'error'}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                {/* M-6: only the Grand Total renders in the always-visible footer. */}
                                <TableRow data-help="statutory.bonus.grandTotal" sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}>
                                    <TableCell colSpan={4}>Grand Total</TableCell>
                                    <TableCell align="right">{money(register.grandTotal.festivalBonusTotal)}</TableCell>
                                    <TableCell align="right">{money(register.grandTotal.attendanceBonusTotal)}</TableCell>
                                    <TableCell colSpan={2}>{money(register.grandTotal.combinedTotal)}</TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={register.rows.length}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    />

                    <Accordion sx={{ mt: 2 }} data-testid="bonus-employee-totals-section" data-help-section="statutory.bonus.employeeTotals">
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Employee Totals ({register.employeeTotals.length})</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell data-help="statutory.bonus.totals.col.employee">Employee</TableCell>
                                            <TableCell align="right" data-help="statutory.bonus.totals.col.festival">Festival Bonus</TableCell>
                                            <TableCell align="right" data-help="statutory.bonus.totals.col.attendance">Attendance Bonus</TableCell>
                                            <TableCell align="right" data-help="statutory.bonus.totals.col.combined">Combined</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pagedTotals.map(t => (
                                            <TableRow key={t.userId}>
                                                <TableCell>{t.employeeName ?? t.userId}</TableCell>
                                                <TableCell align="right">{money(t.festivalBonusTotal)}</TableCell>
                                                <TableCell align="right">{money(t.attendanceBonusTotal)}</TableCell>
                                                <TableCell align="right">{money(t.combinedTotal)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={register.employeeTotals.length}
                                page={totalsPage}
                                onPageChange={(_, p) => setTotalsPage(p)}
                                rowsPerPage={EMPLOYEE_TOTALS_PAGE_SIZE}
                                rowsPerPageOptions={[EMPLOYEE_TOTALS_PAGE_SIZE]}
                            />
                        </AccordionDetails>
                    </Accordion>
                </>
            )}
        </Box>
    );
};

export default BonusRegisterTab;
